import { useState, useEffect, useRef } from 'react';
import { getUnreadNotificationCount, subscribeToNotifications } from '../lib/supabase/notifications';
import { supabase } from '../config/supabase';
import notificationSound from '../lib/soundUtils/notificationSound';
import messageSound from '../lib/soundUtils/messageSound';

/**
 * Custom hook to manage notification badge count with real-time updates.
 * Mirrors the exact pattern of useMessageNotifications so behaviour is identical.
 */
export const useNotificationBadge = (userId) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [latestNotification, setLatestNotification] = useState(null);


    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);


    // Keep mute/volume in refs so the subscription callback always reads the
    // latest value without needing to re-subscribe when settings change.
    const isMutedRef = useRef(false);
    const volumeRef = useRef(0.7);
    const isInitialLoad = useRef(true);

    // Load saved sound settings once on mount
    useEffect(() => {
        const savedVolume = localStorage.getItem('notificationVolume');
        const savedMuted = localStorage.getItem('notificationMuted');

        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    // Single effect — fetch count then subscribe. Only re-runs when userId changes.
    useEffect(() => {
        if (!userId) {
            setUnreadCount(0);
            return;
        }

        isInitialLoad.current = true;

        // Fetch initial count, then mark init done — same pattern as messages
        const fetchCount = async () => {
            try {
                const count = await getUnreadNotificationCount(userId);
                setUnreadCount(count);
            } catch (error) {
                console.error('Error fetching unread notification count:', error);
            } finally {
                isInitialLoad.current = false;
            }
        };

        fetchCount();

        const subscription = subscribeToNotifications(userId, async (newNotification) => {

            const count = await getUnreadNotificationCount(userId);
            setUnreadCount(count);

            // Play sound
            if (!isMuted) {
                messageSound.play(volume);
            }

            // Fire toast only for genuine incoming messages (not on page load)
            if (!isInitialLoad.current) {
                setLatestNotification(newNotification);
            }



        })

        // Poll every 30 seconds as fallback
        const interval = setInterval(async () => {
            const count = await getUnreadNotificationCount(userId);
            setUnreadCount(count);
        }, 60000);


        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
            clearInterval(interval);
        };

        // const subscription = supabase
        //     .channel(channelName)
        //     .on(
        //         'postgres_changes',
        //         {
        //             event: 'INSERT',
        //             schema: 'public',
        //             table: 'notifications',
        //             filter: `user_id=eq.${userId}`
        //         },
        //         (payload) => {
        //             setUnreadCount(prev => prev + 1);

        //             if (!isInitialLoad.current) {
        //                 // Read latest mute/volume from refs — no stale closure
        //                 if (!isMutedRef.current) {
        //                     notificationSound.play(volumeRef.current);
        //                 }
        //                 setLatestNotification({
        //                     title: payload.new?.title || null,
        //                     body: payload.new?.body || payload.new?.message || null,
        //                     notification: payload.new || null,
        //                     timestamp: Date.now(),
        //                 });
        //             }
        //         }
        //     )
        //     .on(
        //         'postgres_changes',
        //         {
        //             event: 'UPDATE',
        //             schema: 'public',
        //             table: 'notifications',
        //             filter: `user_id=eq.${userId}`
        //         },
        //         (payload) => {
        //             if (payload.new.is_read && !payload.old.is_read) {
        //                 setUnreadCount(prev => Math.max(0, prev - 1));
        //             } else if (!payload.new.is_read && payload.old.is_read) {
        //                 setUnreadCount(prev => prev + 1);
        //             }
        //         }
        //     )
        //     .on(
        //         'postgres_changes',
        //         {
        //             event: 'DELETE',
        //             schema: 'public',
        //             table: 'notifications',
        //             filter: `user_id=eq.${userId}`
        //         },
        //         (payload) => {
        //             if (!payload.old.is_read) {
        //                 setUnreadCount(prev => Math.max(0, prev - 1));
        //             }
        //         }
        //     )
        //     .subscribe();

        // return () => {
        //     supabase.removeChannel(subscription);
        // };
    }, [userId]);

    // Called by NotificationMenu when mute/volume settings change
    const updateSoundSettings = () => {
        const savedVolume = localStorage.getItem('notificationVolume');
        const savedMuted = localStorage.getItem('notificationMuted');
        if (savedVolume) volumeRef.current = parseFloat(savedVolume);
        if (savedMuted) isMutedRef.current = savedMuted === 'true';
    };

    const refreshCount = async () => {
        if (!userId) return;
        try {
            const count = await getUnreadNotificationCount(userId);
            setUnreadCount(count);
        } catch (error) {
            console.error('Error refreshing notification count:', error);
        }
    };

    const dismissLatestNotification = () => setLatestNotification(null);

    return {
        unreadCount,
        refreshCount,
        latestNotification,
        dismissLatestNotification,
        updateSoundSettings,
    };
};
